import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  Fuel,
  DollarSign,
  TrendingUp,
  Gauge,
  Plus,
  X,
  Loader2,
} from 'lucide-react';

interface FuelEntry {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  date: string;
  gallons: number;
  cost_per_gallon: number;
  total_cost: number;
  odometer: number;
  fuel_type: string;
  station: string;
  card_number: string;
}

interface FuelResponse {
  entries: FuelEntry[];
  metrics: {
    total_gallons: number;
    total_cost: number;
    avg_cost_per_gallon: number;
    avg_mpg: number;
  };
}

interface VehicleOption { id: string; name: string }

export default function FuelPage() {
  const { data, isLoading, refetch } = useApi<FuelResponse>('/api/fuel');
  const { data: vehiclesData } = useApi<{ vehicles: VehicleOption[] }>('/api/vehicles?pageSize=100');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const metrics = data?.metrics ?? { total_gallons: 0, total_cost: 0, avg_cost_per_gallon: 0, avg_mpg: 0 };
  const entries = data?.entries ?? [];
  const vehicles = vehiclesData?.vehicles ?? [];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    const selectedVehicle = vehicles.find(v => v.id === fd.get('vehicle_id'));
    const gallons = Number(fd.get('gallons'));
    const costPerGallon = Number(fd.get('cost_per_gallon'));
    try {
      await apiFetch('/api/fuel', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: fd.get('vehicle_id'),
          vehicle_name: selectedVehicle?.name ?? '',
          gallons,
          cost_per_gallon: costPerGallon,
          total_cost: gallons * costPerGallon,
          odometer: Number(fd.get('odometer')),
          fuel_type: fd.get('fuel_type') || 'gasoline',
          station: fd.get('station') || undefined,
          card_number: fd.get('card_number') || undefined,
          date: fd.get('date'),
          notes: fd.get('notes') || undefined,
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add fuel entry');
    } finally {
      setSaving(false);
    }
  }, [vehicles, refetch]);

  const metricCards = [
    { label: 'Total Gallons', value: metrics.total_gallons.toLocaleString(), icon: Fuel, color: '#10B981' },
    { label: 'Total Cost', value: `$${metrics.total_cost.toLocaleString()}`, icon: DollarSign, color: '#10B981' },
    { label: 'Avg Cost/Gallon', value: `$${metrics.avg_cost_per_gallon.toFixed(2)}`, icon: TrendingUp, color: '#F59E0B' },
    { label: 'Avg MPG', value: metrics.avg_mpg.toFixed(1), icon: Gauge, color: '#3B82F6' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <Fuel className="h-6 w-6" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Fuel Management</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Track fuel purchases and efficiency</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-foreground transition-colors"
          style={{ background: '#10B981' }}
        >
          <Plus className="h-4 w-4" />
          Add Fuel Entry
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <m.icon className="h-4 w-4" style={{ color: m.color }} />
              <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{m.label}</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: m.color }}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: 'var(--glass-border)', color: 'var(--muted-foreground)' }}>
                <th className="px-4 py-3 font-medium">Vehicle</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Gallons</th>
                <th className="px-4 py-3 font-medium">Cost/Gallon</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Odometer</th>
                <th className="px-4 py-3 font-medium">Station</th>
                <th className="px-4 py-3 font-medium">Card</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: 'var(--muted-foreground)' }} /></td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: 'var(--muted-foreground)' }}>
                    <Fuel className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    <p>No fuel entries found</p>
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="border-b transition-colors hover:bg-white/5" style={{ borderColor: 'var(--glass-border)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{entry.vehicle_name}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: 'var(--foreground)' }}>{entry.gallons.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: 'var(--foreground)' }}>${entry.cost_per_gallon.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono font-medium" style={{ color: 'var(--foreground)' }}>${entry.total_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: 'var(--foreground)' }}>{entry.odometer.toLocaleString()}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{entry.station ?? '--'}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{entry.card_number ?? '--'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Fuel Entry Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Add Fuel Entry</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {formError && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Vehicle" required>
                <select name="vehicle_id" required className="form-select">
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </FormField>
              <FormField label="Date" required>
                <input type="date" name="date" required className="form-input" defaultValue={new Date().toISOString().slice(0, 10)} />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Gallons" required>
                  <input type="number" name="gallons" required step="0.01" min="0.01" className="form-input" placeholder="12.50" />
                </FormField>
                <FormField label="Cost per Gallon ($)" required>
                  <input type="number" name="cost_per_gallon" required step="0.001" min="0" className="form-input" placeholder="3.499" />
                </FormField>
              </div>
              <FormField label="Odometer Reading" required>
                <input type="number" name="odometer" required min="0" className="form-input" placeholder="45000" />
              </FormField>
              <FormField label="Fuel Type">
                <select name="fuel_type" className="form-select" defaultValue="gasoline">
                  {['gasoline', 'diesel', 'electric', 'hybrid'].map(t =>
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  )}
                </select>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Station">
                  <input type="text" name="station" className="form-input" placeholder="Shell, BP, etc." />
                </FormField>
                <FormField label="Fuel Card #">
                  <input type="text" name="card_number" className="form-input" placeholder="Card number" />
                </FormField>
              </div>
              <FormField label="Notes">
                <textarea name="notes" rows={2} className="form-input" placeholder="Additional notes..." />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: '#10B981', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Add Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
